const MS_PER_SECOND = 1000;
const MS_PER_DAY = 24 * 60 * 60 * MS_PER_SECOND;

export const EXPORT_WINDOW_CAP_DAYS = 30;

const CAP_MS = EXPORT_WINDOW_CAP_DAYS * MS_PER_DAY;

export type ExportWindow = {
  dateTimeFrom: string;
  dateTimeTo: string;
};

export type CappedExportWindow = ExportWindow & {
  /** True when the requested window was longer than the cap and got trimmed. */
  narrowed: boolean;
  /** Span of the window the user actually asked for, for the narrowing notice. */
  requestedDays: number;
};

function toSecondPrecisionUtc(instant: Date): string {
  return instant.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * The client's copy of the export's 30-day cap, so the modal can state the
 * window the server will actually read before the request is sent.
 *
 * MIRRORS `capExportWindowToMostRecentThirtyDays` in
 * `services/rest-api/src/ai-insights/use-cases/discoverability/domain/services/exportWindowCap.ts`.
 * The server is authoritative — it caps whatever arrives — but the user is told
 * about the narrowing here, so the two must agree. A change to one is a change
 * to the other; the server's copy carries the same note pointing back here.
 *
 * `dateTimeTo` names an inclusive whole second, so the window's duration runs to
 * one second past it — the same convention `scopeWindow` uses server-side.
 */
export function capExportWindowToThirtyDays(
  requested: ExportWindow
): CappedExportWindow {
  const from = new Date(requested.dateTimeFrom).valueOf();
  const toExclusive = new Date(requested.dateTimeTo).valueOf() + MS_PER_SECOND;
  const requestedMs = toExclusive - from;
  const requestedDays = Math.round(requestedMs / MS_PER_DAY);

  if (requestedMs <= CAP_MS) {
    return { ...requested, narrowed: false, requestedDays };
  }

  return {
    dateTimeFrom: toSecondPrecisionUtc(new Date(toExclusive - CAP_MS)),
    dateTimeTo: requested.dateTimeTo,
    narrowed: true,
    requestedDays,
  };
}
